import { useNavigate } from 'react-router-dom'

export function PrivacyPolicy() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <section>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>

            <p>
              This Privacy Policy describes how Book Manager ("we", "our", or "app") collects, uses,
              and shares your personal information when you use our application.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-semibold mt-6 mb-3">1.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account information: email address and password for authentication</li>
              <li>Book data: titles, authors, ISBNs, genres, notes, and reading status</li>
              <li>Preferences: language settings, display preferences</li>
              <li>User-generated content: book reviews, ratings, and personal notes</li>
              <li>
                Billing information: subscription preferences and billing history (payment details
                are processed by Stripe)
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">
              1.2 Information Collected Automatically
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Usage data: pages visited, features used, interaction patterns</li>
              <li>Device information: device type, operating system, browser type</li>
              <li>Error logs: crash reports and performance data</li>
              <li>Analytics data: app performance metrics and usage statistics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve our service</li>
              <li>Synchronize your book library across devices</li>
              <li>Process subscription payments and manage billing</li>
              <li>Provide customer support and respond to your inquiries</li>
              <li>Send transactional emails related to your account and billing</li>
              <li>Personalize your experience and provide recommendations</li>
              <li>Communicate service updates, security alerts, and support messages</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Analyze usage patterns to improve app functionality</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Information Sharing</h2>
            <p>
              We do not sell, rent, or share your personal information with third parties, except:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Service Providers:</strong> We use Supabase for data storage and
                authentication, Stripe for payment processing, Sentry for error monitoring, and
                PostHog for analytics
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or
                governmental authority
              </li>
              <li>
                <strong>Protection of Rights:</strong> To protect our rights, property, safety, or
                that of our users
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or
                sale of assets
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly agree to sharing
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your
              information:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Encryption of data in transit using HTTPS</li>
              <li>Secure authentication through Supabase Auth</li>
              <li>Regular security assessments and updates</li>
              <li>Limited access to personal data on a need-to-know basis</li>
              <li>Secure data storage with regular backups</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our services
              and fulfill the purposes described in this policy. When you delete your account, we
              will delete or anonymize your personal information within 30 days, unless we are
              required to retain it for legal purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Your Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Access:</strong> Request a copy of your personal information
              </li>
              <li>
                <strong>Correction:</strong> Update or correct inaccurate information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal information
              </li>
              <li>
                <strong>Portability:</strong> Receive your data in a portable format
              </li>
              <li>
                <strong>Restriction:</strong> Limit how we process your information
              </li>
              <li>
                <strong>Objection:</strong> Object to certain processing activities
              </li>
              <li>
                <strong>Withdrawal:</strong> Withdraw previously given consent
              </li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us through the app's support channel.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">
              7. Cookies and Tracking Technologies
            </h2>
            <p>We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Keep you signed in to the application</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze app usage and performance</li>
              <li>Provide personalized content</li>
            </ul>
            <p className="mt-3">You can manage cookie preferences through your browser settings.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Children's Privacy</h2>
            <p>
              Our service is not directed to children under 13 years of age. We do not knowingly
              collect personal information from children under 13. If you believe we have collected
              information from a child under 13, please contact us immediately so we can delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own.
              These countries may have different data protection laws. We ensure appropriate
              safeguards are in place to protect your information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">10. California Privacy Rights</h2>
            <p>
              California residents have additional rights under the California Consumer Privacy Act
              (CCPA):
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">11. EU/UK Privacy Rights</h2>
            <p>
              If you are located in the European Union or United Kingdom, you have additional rights
              under the General Data Protection Regulation (GDPR), including the right to lodge a
              complaint with your local data protection authority.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new policy on this page and updating the "Last
              updated" date. Your continued use of the app after changes constitutes acceptance of
              the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact
              us at:
            </p>
            <div className="mt-3 pl-6">
              <p>Email: contact@prandogabriel.dev</p>
              <p>Address: Brazil</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
